"use client";

/**
 * V4 design primitives; hairline-bordered, soft-filled, label above input.
 *
 * These use theme tokens rather than literal hexes so the flow tracks light
 * and dark mode. The literals this file used to carry were copies of the
 * cream palette's values, so they silently stopped matching the page the
 * moment those tokens were repointed at the neutral system.
 *
 * Tokens:
 *   bg-background       page bg
 *   bg-muted            input fill
 *   border-border       hairline border
 *   text-foreground     primary text
 *   neutral-500 / neutral-400 (dark)  tertiary, counters, placeholders
 *   marketing-blue      focus + CTA
 *
 * Secondary text is the one exception: it stays the literal `text-[#606060]`
 * paired with `dark:text-neutral-400`, because `--muted-foreground` resolves to
 * `#1a1a1a` in light (identical to `--foreground`), so `text-muted-foreground`
 * would erase the distinction between a heading and its subtitle. Collapse
 * these once that variable carries a real secondary value.
 */

import { useTranslations } from "next-intl";

interface FieldProps {
  label: string;
  optional?: boolean;
  count?: string | null;
  hint?: string;
  children: React.ReactNode;
}

export function Field({ label, optional, count, hint, children }: FieldProps) {
  const t = useTranslations("Join.fields");
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[11px] tracking-[0.04em] text-[#606060] uppercase dark:text-neutral-400">
          {label}
          {optional && (
            <span className="ml-1.5 text-neutral-500 dark:text-neutral-400">
              {t("optional")}
            </span>
          )}
        </span>
        {count != null && (
          <span className="font-mono text-[11px] text-neutral-500 dark:text-neutral-400">
            {count}
          </span>
        )}
      </div>
      {children}
      {hint && (
        <p className="text-[12.5px] leading-[1.4] text-neutral-500 dark:text-neutral-400">
          {hint}
        </p>
      )}
    </div>
  );
}

const inputClasses =
  "w-full rounded-lg border border-border bg-muted px-3.5 py-3 text-[16px] tracking-[-0.01em] text-foreground outline-none transition-colors placeholder:text-neutral-500 dark:placeholder:text-neutral-400 focus:border-marketing-blue dark:focus:border-marketing-blue-light focus:shadow-[0_0_0_3px_rgba(10,6,247,0.1)] sm:text-[15px]";

const monoInputClasses =
  "w-full rounded-lg border border-border bg-muted px-3.5 py-3 font-mono text-[16px] text-foreground outline-none transition-colors placeholder:text-neutral-500 dark:placeholder:text-neutral-400 focus:border-marketing-blue dark:focus:border-marketing-blue-light focus:shadow-[0_0_0_3px_rgba(10,6,247,0.1)] sm:text-[14px]";

interface InputProps {
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  type?: "text" | "url" | "tel" | "email" | "number";
  mono?: boolean;
  autoComplete?: string;
}

export function Input({
  value,
  placeholder,
  onChange,
  type = "text",
  mono,
  autoComplete,
}: InputProps) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      autoComplete={autoComplete}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      className={mono ? monoInputClasses : inputClasses}
    />
  );
}

interface RateInputProps {
  value: string;
  /** Currency symbol for the expert's residence, rendered as a fixed prefix. */
  symbol: string;
  onChange: (value: string) => void;
  onBlur: () => void;
}

export function RateInput({ value, symbol, onChange, onBlur }: RateInputProps) {
  const t = useTranslations("Join.fields");
  return (
    <div className="border-border bg-muted focus-within:border-marketing-blue dark:focus-within:border-marketing-blue-light flex w-full items-center gap-2 rounded-xl border px-5 py-4 transition-colors focus-within:shadow-[0_0_0_3px_rgba(10,6,247,0.1)]">
      <span className="text-[26px] leading-none font-medium tracking-[-0.02em] text-[#606060] sm:text-[30px] dark:text-neutral-400">
        {symbol}
      </span>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        placeholder="0"
        onChange={(e) => {
          onChange(e.target.value);
        }}
        onBlur={onBlur}
        className="text-foreground min-w-0 flex-1 bg-transparent text-[26px] leading-none font-medium tracking-[-0.02em] outline-none placeholder:text-neutral-400 sm:text-[30px] dark:placeholder:text-neutral-500"
      />
      <span className="shrink-0 font-mono text-[11px] tracking-[0.04em] text-[#606060] uppercase dark:text-neutral-400">
        {t("perHour")}
      </span>
    </div>
  );
}

interface TextareaProps {
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  rows?: number;
  /** Tailwind min-height override, e.g. "min-h-[180px]". Defaults to 96px. */
  minHeightClass?: string;
}

export function Textarea({
  value,
  placeholder,
  onChange,
  rows = 4,
  minHeightClass = "min-h-[96px]",
}: TextareaProps) {
  return (
    <textarea
      value={value}
      placeholder={placeholder}
      rows={rows}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      className={`focus:border-marketing-blue dark:focus:border-marketing-blue-light ${minHeightClass} border-border bg-muted text-foreground w-full resize-y rounded-lg border px-3.5 py-3 text-[16px] leading-[1.55] tracking-[-0.01em] transition-colors outline-none placeholder:text-neutral-500 focus:shadow-[0_0_0_3px_rgba(10,6,247,0.1)] sm:text-[15px] dark:placeholder:text-neutral-400`}
    />
  );
}
